import { ViewController } from '@estee/elc-service';
import { SearchLoadedUseCase } from '../use-cases/SearchLoadedUseCase';
import { SearchContentLoadedUseCase } from '../use-cases/SearchContentLoadedUseCase';
import { SearchTypeaheadUseCase } from '../use-cases/SearchTypeaheadUseCase';
import { SearchRepository } from '../repositories/SearchRepository';
import { computed, action, observable } from 'mobx';
import {
    MIN_SEARCH_LENGTH,
    DEBOUNCE_LIMIT,
    SEARCH_TYPE_PARAM,
    PREDICTIVE_SEARCH_PARAM,
    SEARCH_TEXT_PARAM,
    TAB_TYPE,
    PREDICTIVE_CONTENT_SEARCH_PARAM,
    PREDICTIVE_PRODUCTS_SEARCH_PARAM,
    CONTENT_SEARCH_ITEMS_LIMIT,
    RESULTS_MESSAGE,
    OVERLAY_PRODUCT_RESULTS_LIMIT,
    SEARCH_REDIRECT_PARAM,
    SEARCH_PARAM
} from '../constants/Search';
import {
    IEnhancedConfig,
    IPropsSearchConfig,
    IContentEnabledConfig
} from '../../internal/interfaces/ISearchInterfaces';
import { events, emit } from '@estee/elc-service-bus';
import { Config } from '../domain/entities/Config';

const {
    NO_RESULTS,
    RESULTS_FOUND_OVERLAY,
    RESULTS_ADJUSTMENT,
    STORIES_RESULT_OVERLAY,
    NO_MESSAGE
} = RESULTS_MESSAGE;

export class SearchGnavInputFieldViewController extends ViewController {
    @observable private searchTerm: string;
    @observable private searchRepository: SearchRepository;
    @observable public isSearchOverlayOpen: boolean = false;
    @observable public shouldFocusOverlayInput: boolean = false;
    @observable private inputValue: string = '';
    @observable private disableInput: boolean = false;
    @observable private searchDisplayFormat: string;
    @observable private currentTab: string = TAB_TYPE.PRODUCTS;
    @observable public configSearch: Config;
    @observable private enableContentSearch: boolean = false;
    @observable private onlyFavoritesEnabled: boolean = false;
    @observable private onlyTrendingTermsEnabled: boolean = false;
    @observable private enhancedSearchEnabled: boolean = false;
    @observable private enableContentArticle: boolean = false;
    @observable private enableContentTout: boolean = false;
    @observable private enableContentVideo: boolean = false;
    @observable private enableContentBanner: boolean = false;

    private minSearchLength: number = MIN_SEARCH_LENGTH;
    private debounceLimit: number = DEBOUNCE_LIMIT;
    private searchResetTimeout: number;

    constructor(searchRepository: SearchRepository, config: Config) {
        super();
        this.searchRepository = searchRepository;
        this.configSearch = config;
        this.isLoading = false;
    }

    @computed
    get data() {
        return {
            productIds: this.productIds,
            skuIds: this.skuIds,
            limitItems: this.limitItems,
            items: this.items,
            isSearchOverlayOpen: this.isSearchOverlayOpen,
            shouldFocusOverlayInput: this.shouldFocusOverlayInput,
            inputValue: this.inputValue,
            resultsLoaded: this.searchRepository.isTypeaheadProductsLoaded,
            disableInput: this.disableInput,
            typeaheadResults: this.typeaheadResults,
            limitedTypeaheadResults: this.limitedTypeaheadResults,
            numRecs: this.numRecs,
            adjustedTerm: this.adjustedTerm,
            configSearch: this.configSearch.config,
            dynamicTouts: this.dynamicTouts,
            dynamicArticles: this.dynamicArticles,
            contentRecs: this.contentRecs,
            currentTab: this.getCurrentTab,
            articlesRecsNum: this.articlesRecsNum,
            productRecsNum: this.productRecsNum,
            isSearchButtonVisible: this.isSearchButtonVisible,
            searchType: this.searchType,
            onlyFavoritesEnabled: this.onlyFavoritesEnabled,
            onlyTrendingTermsEnabled: this.onlyTrendingTermsEnabled,
            showEnhancedSearch: this.showEnhancedSearch,
            resultType: this.resultType,
            overlayArticlesRecordCount: this.overlayArticlesRecordCount,
            overlayProductsRecordCount: this.overlayProductsRecordCount,
            showSearchGrid: this.showSearchGrid
        };
    }

    @computed
    public get searchType() {
        const isStoriesTab = this.currentTab === TAB_TYPE.STORIES;
        if (this.enableContentSearch && isStoriesTab) {
            return PREDICTIVE_CONTENT_SEARCH_PARAM;
        }

        return PREDICTIVE_SEARCH_PARAM.concat(SEARCH_TEXT_PARAM);
    }

    @computed
    private get isSearchButtonVisible() {
        return Boolean(
            (this.productRecsNum || this.articlesRecsNum || this.endecaRedirectUrl) &&
                this.inputValue.length
        );
    }

    @computed
    public get endecaRedirectUrl() {
        return this.searchRepository.searchResults.endecaRedirectUrl;
    }

    private get showEnhancedSearch() {
        return this.enhancedSearchEnabled && !(this.endecaRedirectUrl && this.inputValue);
    }

    @computed
    public get showSearchGrid() {
        return this.isLoading || this.searchRepository.isTypeaheadProductsLoaded;
    }

    @computed
    private get productIds() {
        return this.searchRepository.searchResults.productIds || [];
    }

    @computed
    private get skuIds() {
        return this.searchRepository.searchResults.skuIds || [];
    }

    @computed
    private get items() {
        return this.searchRepository.items || [];
    }

    @computed
    private get limitItems() {
        return this.items.slice(0, OVERLAY_PRODUCT_RESULTS_LIMIT);
    }

    @computed
    private get limitProductIds() {
        return this.productIds.slice(0, OVERLAY_PRODUCT_RESULTS_LIMIT);
    }

    @computed
    private get dynamicTouts() {
        return this.searchRepository.searchContentCatalog.touts || [];
    }

    @computed
    private get dynamicArticles() {
        return this.searchRepository.searchArticleCatalog.articles || [];
    }

    @action
    public setMinSearchLength(value: number) {
        this.minSearchLength = value;
    }

    @action
    public setSearchDisplayFormat(value: string) {
        this.searchDisplayFormat = value;
    }

    @computed
    private get typeaheadResults() {
        return this.searchRepository.typeahead.dimensionsTypeahead || [];
    }

    @action
    public setContentSearch(
        enableContentSearch: boolean,
        contentEnabledConfig: IContentEnabledConfig = {}
    ) {
        this.enableContentSearch = enableContentSearch;

        if (contentEnabledConfig.enableContentArticle) {
            this.enableContentArticle = contentEnabledConfig.enableContentArticle;
        }
        if (contentEnabledConfig.enableContentTout) {
            this.enableContentTout = contentEnabledConfig.enableContentTout;
        }
        if (contentEnabledConfig.enableContentVideo) {
            this.enableContentVideo = contentEnabledConfig.enableContentVideo;
        }
        if (contentEnabledConfig.enableContentBanner) {
            this.enableContentBanner = contentEnabledConfig.enableContentBanner;
        }
    }

    @computed
    private get limitedTypeaheadResults() {
        return this.typeaheadResults.slice(0, 5);
    }

    @action
    public setCurrentContentTab = (id: string) => {
        this.currentTab = id;
    };

    @action
    public searchResults = async () => {
        if (!!this.searchTerm) {
            const search = {
                searchTerm: this.searchTerm,
                searchDisplayFormat: this.searchDisplayFormat
            };

            const searchLoadedUseCase = new SearchLoadedUseCase(this.searchRepository, search);
            const searchTypeaheadUseCase = new SearchTypeaheadUseCase(
                this.searchRepository,
                search
            );
            try {
                this.isLoading = true;
                this.searchRepository.clearRepositoryItems();
                this.searchRepository.searchTypeaheadCatalog.dimensionsTypeahead = [];
                this.setCurrentContentTab(TAB_TYPE.PRODUCTS);
                await searchLoadedUseCase.execute();
                await searchTypeaheadUseCase.execute();
                if (this.enableContentSearch) {
                    const contentSearch = {
                        searchTerm: this.searchTerm,
                        contentEnabledConfig: {
                            enableContentArticle: this.enableContentArticle,
                            enableContentTout: this.enableContentTout,
                            enableContentVideo: this.enableContentVideo,
                            enableContentBanner: this.enableContentBanner
                        }
                    };
                    const searchContentLoadedUseCase = new SearchContentLoadedUseCase(
                        this.searchRepository,
                        contentSearch
                    );
                    await searchContentLoadedUseCase.execute();
                }

                if (this.items.length === 0 && this.dynamicArticles.length) {
                    this.setCurrentContentTab(TAB_TYPE.STORIES);
                }

                this.trackSearchResults();
            } finally {
                this.isLoading = false;
            }
        }
    };

    @action
    public toggleSearchOverlay = () => {
        if (this.isSearchOverlayOpen) {
            this.closeSearchOverlay();
        } else {
            this.openSearchOverlay();
        }
    };

    @action
    public openSearchOverlay = () => {
        this.isSearchOverlayOpen = true;
        this.setShouldFocusOverlayInput(true);
    };

    @action
    public closeSearchOverlay = () => {
        this.clearInputValue();
        this.isSearchOverlayOpen = false;
    };

    @action
    public setShouldFocusOverlayInput = (shouldFocusOverlayInput: boolean) => {
        this.shouldFocusOverlayInput = shouldFocusOverlayInput;
    };

    public searchTermSelected = (searchTerm: string) => () => {
        this.trackSearchTermSelected(searchTerm);
    };

    @action
    public clearInputValue = () => {
        this.inputValue = '';
    };

    @action
    private debounceSearchResults = () => {
        clearTimeout(this.searchResetTimeout);
        this.searchResetTimeout = window.setTimeout(() => this.searchResults(), this.debounceLimit);
    };

    public searchQuery(value: string) {
        this.searchTerm = value;
    }

    @action
    public initialize = (props: IPropsSearchConfig) => {
        this.setSearchDisplayFormat(props.searchDisplayFormat);

        if (props.minSearchLength) {
            this.setMinSearchLength(props.minSearchLength);
        }

        if (props.enableContentSearch) {
            this.setContentSearch(props.enableContentSearch, props.contentEnabledConfig);
        }

        if (props.enhancedConfig) {
            this.setEnhancedSearchFormat(props.enhancedConfig);
        }
    };

    @action
    public setEnhancedSearchFormat = (config: IEnhancedConfig) => {
        const { favoriteProducts, terms } = config;

        if (!terms || !terms.length) {
            this.onlyFavoritesEnabled = true;
        }

        if (!favoriteProducts || !favoriteProducts.length) {
            this.onlyTrendingTermsEnabled = true;
        }

        this.setShowEnhancedSearch(config);
    };

    private setShowEnhancedSearch = (config: IEnhancedConfig) => {
        const { toutItem, favoriteProducts, terms } = config;

        const hasProducts =
            favoriteProducts && favoriteProducts.length >= CONTENT_SEARCH_ITEMS_LIMIT;
        const hasToutItem = toutItem && toutItem.length;
        const hasProductsAndToutItem =
            favoriteProducts && favoriteProducts.length >= 2 && hasToutItem;
        const hasTerms = terms && terms.length;

        this.enhancedSearchEnabled = Boolean(hasProducts || hasProductsAndToutItem || hasTerms);
    };

    @action
    public handleChange = (event: { value: string }): void => {
        this.searchRepository.setSearchResultsLoaded(false);
        this.inputValue = event.value;
        if (this.inputValue.length >= this.minSearchLength) {
            if (!this.isSearchOverlayOpen) {
                this.toggleSearchOverlay();
            }
            this.searchTerm = this.inputValue;
            this.debounceSearchResults();
        }
    };

    @computed
    private get numRecs() {
        const records = {
            contentTotalNumRecs: -1,
            lastRecNum: -1,
            totalNumRecs: -1
        };
        const { totalNumRecs } = this.searchRepository.searchResults;

        records.totalNumRecs = totalNumRecs;
        records.contentTotalNumRecs = this.searchRepository.searchResults.numRecs.contentTotalNumRecs;
        records.lastRecNum = this.searchRepository.searchResults.lastRecNum;

        return records;
    }

    @computed
    private get overlayArticlesRecordCount() {
        return this.articlesRecsNum > CONTENT_SEARCH_ITEMS_LIMIT
            ? CONTENT_SEARCH_ITEMS_LIMIT
            : this.articlesRecsNum;
    }

    @computed
    private get overlayProductsRecordCount() {
        return this.numRecs.totalNumRecs > OVERLAY_PRODUCT_RESULTS_LIMIT
            ? OVERLAY_PRODUCT_RESULTS_LIMIT
            : this.numRecs.totalNumRecs;
    }

    @computed
    public get resultType() {
        if (isNaN(this.numRecs.totalNumRecs)) {
            return NO_MESSAGE;
        }
        if (this.adjustedTerm && this.getCurrentTab === TAB_TYPE.STORIES) {
            return STORIES_RESULT_OVERLAY;
        }
        if (this.adjustedTerm) {
            return RESULTS_ADJUSTMENT;
        }
        if (this.numRecs.totalNumRecs === 0 && this.articlesRecsNum) {
            return STORIES_RESULT_OVERLAY;
        }
        if (this.numRecs.totalNumRecs === 0) {
            return NO_RESULTS;
        }

        return RESULTS_FOUND_OVERLAY;
    }

    @computed
    private get contentRecs() {
        return this.searchRepository.searchContentCatalog.numContentRecs.contentRecNumber;
    }

    @computed
    private get articlesRecsNum() {
        return this.searchRepository.searchArticleCatalog.numContentRecs.articlesRecNumber;
    }

    @computed
    private get productRecsNum() {
        return (
            this.searchRepository.searchResults.numRecs.contentTotalNumRecs -
            this.dynamicTouts.length
        );
    }

    @computed
    private get adjustedTerm() {
        return this.searchRepository.searchResults.adjustedTerm;
    }

    @action
    public onKeyUp = (
        event: { key: string; value: string },
        siteUrl: string,
        redirect: boolean,
        enableContentSearch: boolean
    ): void => {
        const isSearchValid = Boolean(event.key === 'Enter' && event.value);
        let redirectUrl = siteUrl;

        const conditionalSearch = (
            validSearch: boolean,
            isNotRedirect: boolean,
            callback: () => void
        ) => {
            if (validSearch) {
                this.disableInput = true;
                if (isNotRedirect) {
                    callback();
                }
                emit(events.CHANGE_LOCATION, { redirectUrl });
            }
        };

        conditionalSearch(isSearchValid, !redirect, () => {
            if (enableContentSearch) {
                redirectUrl = `${siteUrl}${SEARCH_TYPE_PARAM}${
                    this.getCurrentTab === TAB_TYPE.PRODUCTS
                        ? PREDICTIVE_PRODUCTS_SEARCH_PARAM
                        : PREDICTIVE_CONTENT_SEARCH_PARAM
                }`;
            } else {
                redirectUrl = `${siteUrl}${SEARCH_TYPE_PARAM}${PREDICTIVE_SEARCH_PARAM}${SEARCH_TEXT_PARAM}`;
            }
        });
    };

    @action
    public onSearchButtonClick = (siteUrl: string): void => {
        let redirectUrl = siteUrl;

        if (this.endecaRedirectUrl) {
            redirectUrl = `${this.endecaRedirectUrl}${SEARCH_PARAM}${this.inputValue}${SEARCH_TYPE_PARAM}${PREDICTIVE_SEARCH_PARAM}${SEARCH_REDIRECT_PARAM}`;
        }

        emit(events.CHANGE_LOCATION, { redirectUrl });
    };

    @action
    public setCurrentTab = (id: string) => {
        this.currentTab = id;
    };

    @computed
    public get getCurrentTab() {
        return this.currentTab;
    }

    private trackSearchResults() {
        const eventArgs = {
            searchTerm: this.searchTerm,
            searchType: 'predictive',
            productIds: this.limitProductIds
        };

        emit(events.SEARCH_PRODUCTS_QUERIED, eventArgs);
    }

    private trackSearchTermSelected(searchTerm: string) {
        emit(events.SEARCH_TERM_SELECTED, { searchTerm });
    }
}
